import { useClient } from '@cortex/react-query';
import { fork } from '@cortex/utils/fork';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { HandlebarsInstructionsEditor } from '../../agents/HandlebarsInstructionsEditor';
import { InfoHint } from '../../common/InfoHint';
import { JsonSchemaBuilder } from '../../common/JsonSchemaBuilder';
import { useWorkflowEditor } from '../WorkflowEditorContext';

import { lockedFieldTitle } from './LockedFieldset';
import { LockedToolList } from './LockedToolList';
import { ToolRefsField } from './ToolRefsField';
import {
  getEffectiveOutputFieldNamesByNodeKey,
  getEffectiveOutputSchemaByNodeKey,
} from './effective-output-schema';
import {
  buildKeyResolution,
  buildUpstreamAvailability,
  captionForSource,
  classifyToken,
  describeTokenState,
  extractTokens,
  getNestedFieldNamesForMappedKey,
  getWorkflowInputKeys,
  getWorkflowInputNestedFields,
  type TokenState,
} from './llm-agent-token-coverage';

import type { INodeConfigProps } from './index';

/**
 * Schema-aware editor for `llm-agent` nodes. The node is self-contained
 * (DEV-16943): model, instructions, tools, and output schema live inline in
 * the node config — no Agent row is referenced. Mirrors the configSchema in
 * packages/node-types/src/llm-agent.
 *
 * Token coverage: when interpolation is on, the node's own `instructions` are
 * scanned and a chip is rendered per `{{token}}`. The goal field gets the same
 * treatment. Coverage logic lives in `llm-agent-token-coverage.ts` so this file
 * stays focused on layout + the React glue.
 */

const chipBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 11,
  padding: '2px 6px',
  borderRadius: 4,
  fontWeight: 500,
};

// Color palette per token state. Centralized so the chip render stays
// a one-liner — `colorsFor(state)` returns the trio to plug into style.
function colorsFor(state: TokenState): { border: string; bg: string; fg: string } {
  switch (state.kind) {
    case 'covered':
      return {
        border: 'var(--status-success, #16a34a)',
        bg: 'var(--surface-success, rgba(22, 163, 74, 0.22))',
        fg: 'var(--status-success, #16a34a)',
      };
    case 'broken':
    case 'missing':
      return {
        border: 'var(--status-danger, #dc2626)',
        bg: 'var(--surface-danger, rgba(220, 38, 38, 0.10))',
        fg: 'var(--status-danger, #dc2626)',
      };
    case 'upstream':
      return {
        border: 'var(--status-warning, #d97706)',
        bg: 'var(--surface-warning, rgba(217, 119, 6, 0.10))',
        fg: 'var(--status-warning, #d97706)',
      };
    case 'unknown':
    default:
      return {
        border: 'var(--status-accent, #1e3a8a)',
        bg: 'var(--surface-accent, rgba(30, 58, 138, 0.10))',
        fg: 'var(--status-accent, #1e3a8a)',
      };
  }
}

/** Popover menu item with a local hover state. */
function MenuItem({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        ...availableMenuItemStyle,
        background: hover ? 'var(--surface-2, rgba(255,255,255,0.06))' : 'transparent',
        color: hover ? 'var(--text-primary)' : 'var(--text-secondary)',
      }}
    >
      {children}
    </button>
  );
}

/**
 * Available-input chip. When the mapped key resolves to an upstream value with
 * a known object schema (nested properties), renders as a dropdown button —
 * click to open a menu listing the whole-object insert plus each leaf field.
 * Otherwise a plain click-to-insert button.
 */
function AvailableTokenChip({
  tokenKey,
  nested,
  onInsert,
  variant = 'mapping',
}: {
  tokenKey: string;
  nested: string[];
  onInsert: (text: string) => void;
  variant?: 'mapping' | 'trigger';
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const isTrigger = variant === 'trigger';
  const chipStyle = isTrigger ? triggerChipStyle : availableChipStyle;
  const tooltip = isTrigger
    ? `Workflow trigger input — click adds it to the input mapping and inserts {{${tokenKey}}}`
    : `Available input — click to insert {{${tokenKey}}}`;
  const prefix = isTrigger ? <span style={triggerBadgeStyle}>trigger</span> : '+ ';

  if (nested.length === 0) {
    return (
      <button
        type="button"
        title={tooltip}
        onClick={() => onInsert(`{{${tokenKey}}}`)}
        style={chipStyle}
      >
        {prefix}
        {`{{${tokenKey}}}`}
      </button>
    );
  }

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        title={tooltip}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={chipStyle}
      >
        {prefix}
        {`{{${tokenKey}}}`} ▾
      </button>
      {open && (
        <div role="menu" style={availableMenuStyle}>
          <MenuItem
            onClick={() => {
              onInsert(`{{${tokenKey}}}`);
              setOpen(false);
            }}
          >
            {`{{${tokenKey}}}`}{' '}
            <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>(whole object)</span>
          </MenuItem>
          {nested.map((leaf) => (
            <MenuItem
              key={leaf}
              onClick={() => {
                onInsert(`{{${tokenKey}.${leaf}}}`);
                setOpen(false);
              }}
            >
              {`{{${tokenKey}.${leaf}}}`}
            </MenuItem>
          ))}
        </div>
      )}
    </div>
  );
}

const availableChipStyle: CSSProperties = {
  ...chipBase,
  cursor: 'pointer',
  border: '1px dashed var(--border-muted, #94a3b8)',
  background: 'transparent',
  color: 'var(--text-secondary, #64748b)',
};

const triggerChipStyle: CSSProperties = {
  ...chipBase,
  cursor: 'pointer',
  border: '1px dashed var(--status-accent, #1d4ed8)',
  background: 'transparent',
  color: 'var(--text-primary)',
  paddingLeft: 4,
};

const triggerBadgeStyle: CSSProperties = {
  display: 'inline-block',
  marginRight: 6,
  padding: '0 4px',
  fontSize: 9,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  borderRadius: 3,
  background: 'var(--surface-accent, rgba(29, 78, 216, 0.18))',
  color: 'var(--status-accent, #1d4ed8)',
};

const chipGroupLabelStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: 'var(--text-secondary)',
};

const chipRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  fontSize: 11,
};

const availableMenuStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  minWidth: 180,
  background: 'var(--surface-1, #1a1a1a)',
  border: '1px solid var(--border-subtle, #2a2a2a)',
  borderRadius: 4,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
  padding: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  zIndex: 10,
};

const availableMenuItemStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '4px 8px',
  borderRadius: 3,
  border: 'none',
  background: 'transparent',
  color: 'var(--text-primary)',
  fontFamily: 'var(--font-mono, monospace)',
  fontSize: 11,
  cursor: 'pointer',
};

interface IModelOption {
  id: string;
  /** `null` for a Formant global default (seed-managed, shared by every org). */
  orgId?: string | null;
  name: string;
  provider: string;
  modelId: string;
}

function modelOptionLabel(m: IModelOption): string {
  return `${m.name} (${m.provider} · ${m.modelId})`;
}

export function LlmAgentConfig({ config, onChange, nodeKey, lockedFields }: INodeConfigProps) {
  const client = useClient();
  const editor = useWorkflowEditor();
  // Fields a managed-workflow template owns on this node (DEV-17471).
  // Instructions and goal are never greyed here: a template that locks them
  // locks the whole config (`*`), which the dispatcher handles.
  const isLocked = (field: string) =>
    !!lockedFields && (lockedFields.has('*') || lockedFields.has(field));
  const hasLocks = !!lockedFields && lockedFields.size > 0;
  const toolsLocked = isLocked('toolRefs') || isLocked('toolAllowlists');

  // Inline config fields (DEV-16943: the node IS the definition).
  const modelConfigId = typeof config.modelConfigId === 'string' ? config.modelConfigId : '';
  const instructions = typeof config.instructions === 'string' ? config.instructions : '';
  const toolRefs = Array.isArray(config.toolRefs) ? (config.toolRefs as string[]) : [];
  const toolAllowlists =
    config.toolAllowlists &&
    typeof config.toolAllowlists === 'object' &&
    !Array.isArray(config.toolAllowlists)
      ? (config.toolAllowlists as Record<string, string[]>)
      : {};
  const outputSchema =
    config.outputSchema &&
    typeof config.outputSchema === 'object' &&
    !Array.isArray(config.outputSchema)
      ? (config.outputSchema as Record<string, unknown>)
      : null;
  const imageDescriptionAllowed = config.imageDescriptionAllowed === true;
  const videoDescriptionAllowed = config.videoDescriptionAllowed === true;
  const interpolate = config.interpolateInstructions === true;

  // Org models plus the Formant global defaults (orgId null) that managed
  // templates reference, so a node pointing at one shows its real selection.
  const modelConfigs = useQuery({
    queryKey: ['model-configs', 'with-global'],
    queryFn: () =>
      client.http.get('v1/model-configs?includeGlobal=true').json<{
        items: IModelOption[];
      }>(),
  });
  const globalModels = (modelConfigs.data?.items ?? []).filter((m) => m.orgId === null);
  const orgModels = (modelConfigs.data?.items ?? []).filter((m) => m.orgId !== null);
  const noModels = modelConfigs.data && modelConfigs.data.items.length === 0;

  // "Prefill from agent" — a one-shot authoring aid that copies an existing
  // agent's definition inline. NOT a live binding: the node stays fully
  // self-contained afterwards. Fetched in-panel so the editor doesn't carry an
  // agent list it otherwise no longer needs.
  const agents = useQuery({
    queryKey: ['agents', 'for-prefill'],
    queryFn: () =>
      client.http.get('v1/agents?limit=200').json<{ items: Array<{ id: string; name: string }> }>(),
  });
  const prefillFromAgent = async (agentId: string) => {
    if (!agentId) return;
    const agent = await client.http.get(`v1/agents/${agentId}`).json<{
      modelConfigId: string | null;
      instructions: string;
      toolRefs: string[];
      toolAllowlists?: Record<string, string[]>;
      outputSchema: Record<string, unknown> | null;
      imageDescriptionAllowed?: boolean;
      videoDescriptionAllowed?: boolean;
    }>();
    onChange({
      modelConfigId: agent.modelConfigId ?? '',
      instructions: agent.instructions ?? '',
      toolRefs: agent.toolRefs ?? [],
      toolAllowlists: agent.toolAllowlists ?? {},
      outputSchema: agent.outputSchema ?? undefined,
      imageDescriptionAllowed: agent.imageDescriptionAllowed ?? false,
      videoDescriptionAllowed: agent.videoDescriptionAllowed ?? false,
    });
  };

  // Shared token classifier — builds the resolution context once and exposes a
  // `classify(token)` that both the goal and the instructions run tokens through.
  const tokenClassifier = useMemo(() => {
    if (!nodeKey) return null;
    const node = editor.getNode(nodeKey);
    const inputMapping = ((node?.data as { inputMapping?: Record<string, unknown> } | undefined)
      ?.inputMapping ?? {}) as Record<string, unknown>;
    const schemaCtx = { nodeTypeOutputSchemaByKey: editor.nodeTypeOutputSchemaByKey };
    const getOutputFieldNamesByNodeKey = (k: string) =>
      getEffectiveOutputFieldNamesByNodeKey(editor.getNode, k, schemaCtx);
    const { keyResolution, source } = buildKeyResolution({
      inputMapping,
      workflowInputSchema: editor.workflowInputSchema,
      getOutputFieldNamesByNodeKey,
    });
    const incomingFromKeys = editor.edges.filter((e) => e.target === nodeKey).map((e) => e.source);
    const upstreamAvailability = buildUpstreamAvailability({
      incomingFromKeys,
      getOutputFieldNamesByNodeKey,
    });
    const availableKeys: string[] = [];
    for (const [k, r] of keyResolution.entries()) {
      if (r === 'resolved') availableKeys.push(k);
    }
    availableKeys.sort();
    const getOutputSchemaByNodeKey = (k: string) =>
      getEffectiveOutputSchemaByNodeKey(editor.getNode, k, schemaCtx);
    const getNestedFields = (key: string): string[] =>
      getNestedFieldNamesForMappedKey(key, {
        inputMapping,
        workflowInputSchema: editor.workflowInputSchema,
        getOutputSchemaByNodeKey,
      });
    const workflowInputKeys = getWorkflowInputKeys(editor.workflowInputSchema);
    const getWorkflowInputNested = (k: string): string[] =>
      getWorkflowInputNestedFields(editor.workflowInputSchema, k);
    const mappedKeys = new Set(Object.keys(inputMapping));
    return {
      source,
      availableKeys,
      workflowInputKeys,
      mappedKeys,
      getNestedFields,
      getWorkflowInputNested,
      // `goal` is injected by THIS node's own `goal` field — always covered.
      classify: (token: string): TokenState => {
        const head = token.split('.')[0] ?? token;
        if (head === 'goal') return { kind: 'covered' };
        return classifyToken({ token, keyResolution, source, upstreamAvailability });
      },
    };
    // editor.nodes / edges drive re-eval on graph edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nodeKey,
    editor,
    editor.nodes,
    editor.edges,
    editor.workflowInputSchema,
    editor.nodeTypeOutputSchemaByKey,
  ]);

  // Coverage chips for the node's instructions — only when `interpolate` is on
  // (otherwise tokens reach the LLM raw).
  const coverage = useMemo(() => {
    if (!tokenClassifier) return null;
    const tokens = extractTokens(instructions);
    if (tokens.length === 0) return null;
    return {
      tokens,
      states: tokens.map((t) => ({ token: t, state: tokenClassifier.classify(t) })),
      source: tokenClassifier.source,
    };
  }, [tokenClassifier, instructions]);

  // Coverage chips for the goal field (see the original three-group design:
  // referenced tokens, available keys, trigger suggestions). `{{goal}}` itself
  // is filtered out — the field is its own substitute.
  const goalCoverage = useMemo(() => {
    if (!tokenClassifier) return null;
    const goalText = typeof config.goal === 'string' ? config.goal : '';
    const tokens = extractTokens(goalText).filter((t) => t !== 'goal');
    const referenced = new Set(tokens.map((t) => t.split('.')[0] ?? t));
    const available = tokenClassifier.availableKeys.filter((k) => !referenced.has(k));
    const triggerSuggestions = tokenClassifier.workflowInputKeys.filter(
      (k) => !referenced.has(k) && !tokenClassifier.mappedKeys.has(k),
    );
    if (tokens.length === 0 && available.length === 0 && triggerSuggestions.length === 0) {
      return null;
    }
    return {
      tokens,
      states: tokens.map((t) => ({ token: t, state: tokenClassifier.classify(t) })),
      source: tokenClassifier.source,
      available,
      triggerSuggestions,
    };
  }, [tokenClassifier, config.goal]);

  const insertIntoGoal = (text: string): void => {
    const current = typeof config.goal === 'string' ? config.goal : '';
    const sep = current.length === 0 || /\s$/.test(current) ? '' : ' ';
    onChange({ goal: `${current}${sep}${text}` });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      {/* Model + instructions + tools + output schema all define THIS node
          inline — grouped in one accent card so it reads as the node's own
          definition, not a reference to something elsewhere. */}
      <div
        className="card"
        style={{
          padding: 'var(--sp-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
          background: 'var(--surface-active)',
          borderColor: 'var(--horizon-glow)',
          boxShadow: 'var(--shadow-glow), var(--surface-highlight)',
          overflow: 'visible',
        }}
      >
        <div className="field">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span className="field-label">Model</span>
            {/* Prefill overwrites tools and output schema too, so it is not
                offered on a node with template-locked fields. */}
            {!hasLocks && agents.data && agents.data.items.length > 0 ? (
              <select
                className="select-native"
                aria-label="Prefill fields from an existing agent"
                value=""
                onChange={(e) => {
                  fork(prefillFromAgent(e.target.value));
                  e.target.value = '';
                }}
                style={{ maxWidth: 220 }}
              >
                <option value="">Prefill from agent…</option>
                {agents.data.items.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
          <select
            className="select-native"
            value={modelConfigId}
            onChange={(e) => onChange({ modelConfigId: e.target.value })}
            disabled={isLocked('modelConfigId') || modelConfigs.isLoading || noModels}
            title={isLocked('modelConfigId') ? lockedFieldTitle : undefined}
          >
            <option value="">
              {modelConfigs.isLoading ? 'Loading models…' : '— Select a model —'}
            </option>
            {orgModels.length > 0 ? (
              <optgroup label="Your models">
                {orgModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {modelOptionLabel(m)}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {globalModels.length > 0 ? (
              <optgroup label="Formant defaults">
                {globalModels.map((m) => (
                  <option key={m.id} value={m.id}>
                    {modelOptionLabel(m)}
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
          {noModels ? (
            <span className="field-help" style={{ color: 'var(--status-warning)' }}>
              No model configs in this org yet — create one in <code>/model-configs</code> first.
            </span>
          ) : !modelConfigId ? (
            <span className="field-help" style={{ color: 'var(--status-warning)' }}>
              A model is required for this node to run.
            </span>
          ) : null}
        </div>

        <label className="field">
          <span className="field-label">
            Instructions
            <InfoHint
              label="How instructions are used"
              text={
                <>
                  The system prompt for this node&apos;s LLM. When <em>Interpolate node input</em>{' '}
                  is on, it&apos;s rendered as a Handlebars template against the node&apos;s input
                  before being sent.
                </>
              }
            />
          </span>
          <HandlebarsInstructionsEditor
            value={instructions}
            onChange={(next) => onChange({ instructions: next })}
            rows={8}
            placeholder="You are a thorough investigator. Analyze the input and…"
            showReferences={false}
          />
        </label>

        {/* A locked tool set is shown, not offered: no remove glyph, no add
            controls (DEV-17471). */}
        {toolsLocked ? (
          <LockedToolList toolRefs={toolRefs} toolAllowlists={toolAllowlists} />
        ) : (
          <ToolRefsField
            toolRefs={toolRefs}
            toolAllowlists={toolAllowlists}
            onChange={(next) =>
              onChange({ toolRefs: next.toolRefs, toolAllowlists: next.toolAllowlists })
            }
          />
        )}

        <div className="field" title={isLocked('outputSchema') ? lockedFieldTitle : undefined}>
          <span className="field-label">
            Output format
            <InfoHint
              label="Structured vs free-text output"
              text="Free-text: the LLM replies in plain prose — downstream nodes receive a single string. Structured JSON: the LLM is forced to produce JSON matching the schema you define — downstream nodes see typed fields."
            />
          </span>
          <div style={{ display: 'flex', gap: 'var(--sp-3)', marginBottom: 'var(--sp-2)' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-1)',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name={`output-format-${nodeKey}`}
                checked={outputSchema === null}
                disabled={isLocked('outputSchema')}
                onChange={() => onChange({ outputSchema: undefined })}
              />
              <span className="t-sm">Free-text response</span>
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-1)',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name={`output-format-${nodeKey}`}
                checked={outputSchema !== null}
                disabled={isLocked('outputSchema')}
                onChange={() =>
                  onChange({
                    outputSchema: outputSchema ?? { type: 'object', properties: {} },
                  })
                }
              />
              <span className="t-sm">Structured JSON</span>
            </label>
          </div>
          {outputSchema !== null && (
            <JsonSchemaBuilder
              value={outputSchema}
              readOnly={isLocked('outputSchema')}
              onChange={(next) => {
                if (isLocked('outputSchema')) return;
                onChange({ outputSchema: next ?? undefined });
              }}
            />
          )}
        </div>

        <label
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            className="checkbox"
            checked={imageDescriptionAllowed}
            disabled={isLocked('imageDescriptionAllowed')}
            title={isLocked('imageDescriptionAllowed') ? lockedFieldTitle : undefined}
            onChange={(e) => onChange({ imageDescriptionAllowed: e.target.checked })}
          />
          <span className="t-sm">Allow image description (exposes the describe_image tool)</span>
        </label>

        <label
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            className="checkbox"
            checked={videoDescriptionAllowed}
            disabled={isLocked('videoDescriptionAllowed')}
            title={isLocked('videoDescriptionAllowed') ? lockedFieldTitle : undefined}
            onChange={(e) => onChange({ videoDescriptionAllowed: e.target.checked })}
          />
          <span className="t-sm">
            Allow video description (exposes describe_video, create_video_storyboard,
            extract_video_frame and get_video_metadata)
          </span>
        </label>
      </div>

      <label className="field">
        <span className="field-label">
          Goal
          <InfoHint
            label="How the goal is used"
            text={
              <>
                Free-text intent for THIS run. When set, it&apos;s added to the node&apos;s
                instructions — appended as <code>Goal: …</code>, or substituted at{' '}
                <code>{`{{goal}}`}</code> if the instructions reference it.
                <br />
                <br />
                The goal is itself a Handlebars template — use <code>{`{{deviceId}}`}</code> etc. to
                pull in upstream values wired into this node&apos;s input mapping. Coverage chips
                below show which tokens will resolve.
              </>
            }
          />
        </span>
        <HandlebarsInstructionsEditor
          value={typeof config.goal === 'string' ? config.goal : ''}
          onChange={(next) => onChange({ goal: next })}
          rows={3}
          placeholder="e.g. Investigate why {{deviceId}} dropped lidar this morning."
          showReferences={false}
        />
        {goalCoverage && (
          <div
            style={{
              marginTop: 'var(--sp-2)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-1)',
            }}
          >
            <span style={chipGroupLabelStyle}>Tokens in goal</span>
            <div style={chipRowStyle}>
              {goalCoverage.states.map(({ token, state }) => {
                const { icon, tooltip } = describeTokenState(state, token);
                const colors = colorsFor(state);
                return (
                  <span
                    key={`ref-${token}`}
                    title={tooltip}
                    style={{
                      ...chipBase,
                      border: `1px solid ${colors.border}`,
                      background: colors.bg,
                      color: colors.fg,
                    }}
                  >
                    {icon} {`{{${token}}}`}
                  </span>
                );
              })}
              {goalCoverage.available.map((key) => (
                <AvailableTokenChip
                  key={`avail-${key}`}
                  tokenKey={key}
                  nested={tokenClassifier?.getNestedFields(key) ?? []}
                  onInsert={(text) => insertIntoGoal(text)}
                />
              ))}
              {goalCoverage.triggerSuggestions.map((key) => (
                <AvailableTokenChip
                  key={`trig-${key}`}
                  tokenKey={key}
                  nested={tokenClassifier?.getWorkflowInputNested(key) ?? []}
                  variant="trigger"
                  onInsert={(text) => {
                    // Auto-wire the trigger field into the node's inputMapping so
                    // the inserted `{{key}}` resolves at runtime.
                    if (nodeKey && !tokenClassifier?.mappedKeys.has(key)) {
                      const node = editor.getNode(nodeKey);
                      const existing = ((
                        node?.data as { inputMapping?: Record<string, unknown> } | undefined
                      )?.inputMapping ?? {}) as Record<string, unknown>;
                      editor.updateNode(nodeKey, {
                        inputMapping: { ...existing, [key]: `$.__input.${key}` },
                      });
                    }
                    insertIntoGoal(text);
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </label>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
          padding: 'var(--sp-3)',
          background: 'var(--surface-2)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--text-secondary)',
          lineHeight: 1.6,
        }}
      >
        <span className="field-label" style={{ marginBottom: 0 }}>
          How inputs reach this node
        </span>
        <p style={{ margin: 0 }}>
          <strong>1. Input mapping</strong> — wire upstream outputs or trigger fields via the{' '}
          <em>Connect</em> panel
          {tokenClassifier && tokenClassifier.mappedKeys.size > 0
            ? ` (${tokenClassifier.mappedKeys.size} key${tokenClassifier.mappedKeys.size === 1 ? '' : 's'} mapped)`
            : ' (none mapped yet)'}
          .
        </p>
        <p style={{ margin: 0 }}>
          <strong>2. Goal field</strong> — free-text intent added to instructions each run; also a
          Handlebars template, so mapped keys resolve here too.
        </p>
        <p style={{ margin: 0 }}>
          <strong>3. Handlebars interpolation</strong> — when enabled below,{' '}
          <code>{`{{key}}`}</code> tokens in instructions and goal are substituted with their mapped
          values at runtime.
        </p>
      </div>

      <label
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', cursor: 'pointer' }}
      >
        <input
          type="checkbox"
          className="checkbox"
          checked={interpolate}
          disabled={isLocked('interpolateInstructions')}
          title={isLocked('interpolateInstructions') ? lockedFieldTitle : undefined}
          onChange={(e) => onChange({ interpolateInstructions: e.target.checked })}
        />
        <span className="t-sm">Interpolate node input into instructions and goal (Handlebars)</span>
      </label>

      {interpolate && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          {coverage === null ? (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              These instructions use no <code>{`{{tokens}}`}</code>. You can leave the interpolation
              flag off.
            </div>
          ) : (
            <>
              <span style={chipGroupLabelStyle}>Tokens in instructions</span>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {captionForSource(coverage.source)}
              </div>
              <div style={chipRowStyle}>
                {coverage.states.map(({ token, state }) => {
                  const { icon, tooltip } = describeTokenState(state, token);
                  const colors = colorsFor(state);
                  return (
                    <span
                      key={token}
                      title={tooltip}
                      style={{
                        ...chipBase,
                        border: `1px solid ${colors.border}`,
                        background: colors.bg,
                        color: colors.fg,
                      }}
                    >
                      {icon} {`{{${token}}}`}
                    </span>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
